import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase.js";
import { forgetRememberedPath } from "./PageMemory.js";
import { setDisplayTimeZone } from "./zone.js";

export interface BusinessMembership {
  business_id: string;
  /** owner = everything; manager = the day-to-day of the whole business; staff = one staff member's own bookings and hours. */
  role: "owner" | "manager" | "staff" | "viewer";
  /** The business's name, for the portal sidebar. */
  business_name: string | null;
  /** The business's time zone (set from its state by the platform admin): every date and time the portal shows follows it. */
  timezone: string | null;
}

interface AuthState {
  session: Session | null;
  loading: boolean;
  isPlatformAdmin: boolean;
  memberships: BusinessMembership[];
  /** The active business for the /app portal. Owners/staff of exactly one business get it
   *  auto-selected; multi-business users pick one (not yet built -- see AppShell). */
  activeBusinessId: string | null;
  setActiveBusinessId: (id: string) => void;
  /** True once signInWithPassword succeeds but the account has a verified TOTP factor that
   *  hasn't been challenged yet THIS session (aal1 -> aal2 required). Login.tsx uses this to
   *  show the code-entry step instead of the password form for an already-started sign-in;
   *  every route gate treats it the same as "not signed in" (Guards.tsx). */
  mfaPending: boolean;
  /** Re-reads the current authenticator assurance level. Call right after mfa.verify()
   *  succeeds -- Supabase's own onAuthStateChange doesn't reliably fire again for that, so
   *  waiting on the subscription alone would leave mfaPending stuck true. */
  refreshMfaStatus: () => Promise<void>;
  /** True from the moment a PASSWORD_RECOVERY auth event fires (an invite-accept or
   *  forgot-password link) until SetPassword.tsx clears it -- see Guards.tsx. */
  needsPasswordSetup: boolean;
  clearPasswordSetup: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  // Distinct from role-loading below: true only once we've heard back AT LEAST ONCE from
  // Supabase about whether there's a session -- including the async work of parsing a
  // magic-link token out of the URL, when landing on a fresh auth callback. Until this is
  // true, `session === null` does NOT mean "not signed in", it means "don't know yet", and
  // must NOT be treated as a reason to redirect to /login (doing so replaces the URL and
  // permanently discards the not-yet-processed token -- this was a real bug: the redirect
  // to /login was firing and wiping the token before Supabase ever got to read it).
  const [sessionChecked, setSessionChecked] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [memberships, setMemberships] = useState<BusinessMembership[]>([]);
  const [activeBusinessId, setActiveBusinessId] = useState<string | null>(null);
  const [aal, setAal] = useState<{ current: string | null; next: string | null } | null>(null);
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  // Which user's memberships are already in state. Supabase re-announces the SAME signed-in user
  // whenever the tab regains focus or a token refreshes (a brand-new Session object each time); that
  // must not look like a new sign-in, or the whole app drops back to "Loading..." and every open page
  // loses its tab, scroll position and half-filled form.
  const rolesLoadedFor = useRef<string | null>(null);
  const userId = session?.user?.id ?? null;

  async function refreshMfaStatus() {
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    setAal(data ? { current: data.currentLevel, next: data.nextLevel } : null);
  }

  useEffect(() => {
    // getSession() itself awaits Supabase's initial session recovery, which includes
    // parsing an access/refresh token or PKCE `code` out of the current URL when present
    // (detectSessionInUrl, on by default) -- so this resolving IS the signal that any
    // invite/recovery-link callback has been fully handled one way or another.
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setSessionChecked(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      // Keep the existing object when it is the same sign-in, so nothing downstream sees a change.
      setSession((prev) => (prev && s && prev.user.id === s.user.id && prev.access_token === s.access_token ? prev : s));
      setSessionChecked(true);
      // Fired for BOTH an invite-accept link and a forgot-password link -- both need the
      // user to set a password before they've really "signed in" (Guards.tsx redirects to
      // /set-password while this is true).
      if (event === "PASSWORD_RECOVERY") setNeedsPasswordSetup(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) {
      setAal(null);
      return;
    }
    void refreshMfaStatus();
  }, [session]);

  useEffect(() => {
    if (!sessionChecked) return; // still don't know yet -- wait, don't guess "not signed in"
    let cancelled = false;
    async function loadRoles() {
      if (!userId) {
        rolesLoadedFor.current = null;
        setIsPlatformAdmin(false);
        setMemberships([]);
        setActiveBusinessId(null);
        setRolesLoading(false);
        return;
      }
      // Same user as before (a refocus or token refresh): their roles are already loaded -- do nothing.
      if (rolesLoadedFor.current === userId) {
        setRolesLoading(false);
        return;
      }
      setRolesLoading(true);
      // Only this person's own rows: RLS also lets an owner or manager read EVERY member row of their business, and without the
      // user_id filter another member's row (e.g. an owner's) was picked up as this person's role.
      const [{ data: adminRow }, { data: memberRows }] = await Promise.all([
        supabase.rpc("is_platform_admin"),
        supabase.from("business_members").select("business_id, role, businesses(name, timezone)").eq("user_id", userId),
      ]);
      if (cancelled) return;
      setIsPlatformAdmin(Boolean(adminRow));
      const list: BusinessMembership[] = ((memberRows ?? []) as unknown as Array<{ business_id: string; role: "owner" | "manager" | "staff" | "viewer"; businesses: { name: string; timezone?: string | null } | null }>).map(
        (m) => ({ business_id: m.business_id, role: m.role, business_name: m.businesses?.name ?? null, timezone: m.businesses?.timezone ?? null })
      );
      setMemberships(list);
      setActiveBusinessId((prev) => prev ?? list[0]?.business_id ?? null);
      rolesLoadedFor.current = userId;
      setRolesLoading(false);
    }
    loadRoles();
    return () => {
      cancelled = true;
    };
  }, [userId, sessionChecked]);

  // Everything the portal shows runs on the active business's own time (lib/zone.ts). Set before the children render, so the first paint is right.
  setDisplayTimeZone(memberships.find((m) => m.business_id === activeBusinessId)?.timezone);

  const loading = !sessionChecked || rolesLoading;
  const mfaPending = aal?.current === "aal1" && aal?.next === "aal2";

  const value = useMemo<AuthState>(
    () => ({
      session,
      loading,
      isPlatformAdmin,
      memberships,
      activeBusinessId,
      setActiveBusinessId,
      mfaPending,
      refreshMfaStatus,
      needsPasswordSetup,
      clearPasswordSetup: () => setNeedsPasswordSetup(false),
      signOut: async () => {
        forgetRememberedPath(); // the next person to sign in on this device starts at their own Dashboard
        await supabase.auth.signOut();
      },
    }),
    [session, loading, isPlatformAdmin, memberships, activeBusinessId, mfaPending, needsPasswordSetup]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Like useAuth, but undefined outside an AuthProvider instead of throwing, for shared pieces that only tweak what they show. */
export function useOptionalAuth(): AuthState | undefined {
  return useContext(AuthContext);
}

/** True for a viewer login (read-only): the pages hide their add, edit, cancel and delete controls. The database refuses the writes anyway. */
export function useReadOnly(): boolean {
  const auth = useOptionalAuth();
  if (!auth || auth.isPlatformAdmin) return false;
  return auth.memberships.find((m) => m.business_id === auth.activeBusinessId)?.role === "viewer";
}
